import { PROMPT_PURPOSE_OPTIONS } from '@/components/prompts/prompt-purpose-utils';
import type { PromptWithVersion } from '@/config/objects/prompts';
import { cn } from '@/lib/utils';
import {
  ApiError,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  FormPageLayout,
  Input,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PromptPurposeValue =
  | 'attraction_short_description'
  | 'attraction_long_description'
  | 'tour_content'
  | 'tour_conclusions'
  | 'themed_short_description'
  | 'themed_long_description'
  | 'attraction_metadata'
  | 'attraction_list'
  | 'attraction_descriptions'
  | 'themed_descriptions';

interface PromptFormData {
  name: string;
  purpose: PromptPurposeValue | '';
  body: string;
  isDefault: boolean;
}

const DEFAULT_FORM: PromptFormData = {
  name: '',
  purpose: '',
  body: '',
  isDefault: false,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parses `{{variableName}}` tokens from a template string.
 *
 * @param text - The prompt body text to scan.
 * @returns A deduplicated, sorted array of variable names found.
 */
function parseVariables(text: string): string[] {
  const matches = text.matchAll(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g);
  const names = new Set<string>();
  for (const match of matches) {
    names.add(match[1]);
  }
  return [...names].sort();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Create / Edit form for a Prompt.
 *
 * - On create: all fields are editable.
 * - On edit: purpose is read-only (purpose cannot change after creation).
 * - Detected `{{variable}}` tokens are displayed as chips below the body textarea.
 */
export function PromptFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const [formData, setFormData] = useState<PromptFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);
  const firstErrorRef = useRef<HTMLDivElement>(null);

  const updateField = useCallback(<K extends keyof PromptFormData>(key: K, value: PromptFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // -----------------------------------------------------------------------
  // Load existing prompt (edit mode)
  // -----------------------------------------------------------------------
  const {
    data: promptResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: PromptWithVersion }>({
    queryKey: ['Prompt', itemId],
    queryFn: () => apiClient.get(`/prompts/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!promptResponse?.data) return;
    const p = promptResponse.data;
    setFormData({
      name: p.name ?? '',
      purpose: p.purpose ?? '',
      body: p.activeVersion?.body ?? '',
      isDefault: p.isDefault ?? false,
    });
    setLastSaved(new Date(p.updatedAt));
    setFormReady(true);
  }, [promptResponse]);

  useEffect(() => {
    if (isError) {
      toast.error('Prompt not found');
      navigate('/settings/prompts');
    }
  }, [isError, navigate]);

  // -----------------------------------------------------------------------
  // Detected variables (live-parsed from body)
  // -----------------------------------------------------------------------
  const detectedVariables = useMemo(() => parseVariables(formData.body), [formData.body]);

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------
  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};

    if (!formData.name.trim()) errs.name = 'Name is required';
    if (formData.name.trim().length < 2) errs.name = 'Name must be at least 2 characters';
    if (!formData.purpose) errs.purpose = 'Purpose is required';
    if (!formData.body.trim()) errs.body = 'Prompt body is required';

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(`field-${firstKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  };

  // -----------------------------------------------------------------------
  // Save mutation
  // -----------------------------------------------------------------------
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: PromptWithVersion }>(`/prompts/${itemId}`, payload);
      }
      return apiClient.post<{ data: PromptWithVersion }>('/prompts', payload);
    },
    onSuccess: (response: { data: PromptWithVersion }) => {
      queryClient.invalidateQueries({ queryKey: ['Prompt'] });
      setLastSaved(new Date());
      if (isEdit) {
        toast.success('Prompt saved successfully');
        navigate(-1);
      } else {
        toast.success('Prompt created successfully');
        navigate(`/settings/prompts/${response.data.id}`);
      }
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.details?.length) {
        const fieldErrors: Record<string, string> = {};
        for (const detail of err.details) {
          const fieldName = detail.path?.[0];
          if (fieldName && !fieldErrors[fieldName]) {
            fieldErrors[fieldName] = detail.message;
          }
        }

        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          const firstKey = Object.keys(fieldErrors)[0];
          const el = document.getElementById(`field-${firstKey}`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          toast.error(`Validation failed for: ${Object.keys(fieldErrors).join(', ')}`);
          return;
        }
      }
      toast.error(err?.message ?? 'Failed to save prompt');
    },
  });

  const handleSave = () => {
    if (!validateForm()) return;

    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      body: formData.body.trim(),
      isDefault: formData.isDefault,
    };

    // Purpose is immutable on edit; only include on create
    if (!isEdit) {
      payload.purpose = formData.purpose;
    }

    saveMutation.mutate(payload);
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(`/settings/prompts/${itemId}`);
    } else {
      navigate('/settings/prompts');
    }
  };

  // -----------------------------------------------------------------------
  // Loading skeleton
  // -----------------------------------------------------------------------
  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-40 w-full" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {isEdit ? 'Edit Prompt' : 'Create Prompt'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? formData.name || 'Update prompt details'
                : 'Add a new reusable AI prompt'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div ref={firstErrorRef} className="space-y-6">
          {/* General Information */}
          <Card className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 border-b">
              <CardTitle className="text-zinc-900">General Information</CardTitle>
              <CardDescription>Core prompt identity</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup name="name" label="Name" required error={errors.name}>
                <Input
                  id="field-name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter prompt name"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup
                name="purpose"
                label="Purpose"
                required={!isEdit}
                description={
                  isEdit
                    ? 'Purpose cannot be changed after creation'
                    : 'Select the generation use case for this prompt'
                }
                error={errors.purpose}
              >
                {isEdit ? (
                  /* Read-only display on edit */
                  <div className="flex h-9 items-center">
                    {promptResponse?.data?.purpose ? (
                      <Badge variant="outline" className="text-sm">
                        {PROMPT_PURPOSE_OPTIONS.find((o) => o.value === promptResponse.data.purpose)?.label ??
                          promptResponse.data.purpose}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">Not set</span>
                    )}
                  </div>
                ) : (
                  <select
                    id="field-purpose"
                    value={formData.purpose}
                    onChange={(e) => updateField('purpose', e.target.value as PromptPurposeValue)}
                    className={cn(
                      'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
                      'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                      errors.purpose && 'border-destructive',
                    )}
                  >
                    <option value="">Select purpose...</option>
                    {PROMPT_PURPOSE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              </FormFieldGroup>

              <div className="flex items-center gap-3">
                <input
                  id="field-isDefault"
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => updateField('isDefault', e.target.checked)}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <label htmlFor="field-isDefault" className="text-sm font-medium leading-none cursor-pointer">
                  Set as default for this purpose
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Prompt Body */}
          <Card className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 border-b">
              <CardTitle className="text-zinc-900">Prompt Body</CardTitle>
              <CardDescription>
                The prompt template. Use{' '}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">{'{{variableName}}'}</code>{' '}
                for dynamic interpolation.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <FormFieldGroup name="body" label="Body" required error={errors.body}>
                <textarea
                  id="field-body"
                  value={formData.body}
                  onChange={(e) => updateField('body', e.target.value)}
                  placeholder="Enter the prompt body..."
                  rows={16}
                  className={cn(
                    'flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm',
                    'font-mono leading-relaxed placeholder:text-muted-foreground',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    errors.body && 'border-destructive',
                  )}
                />
              </FormFieldGroup>

              {/* Detected variable chips */}
              {detectedVariables.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                    Detected variables
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detectedVariables.map((variable) => (
                      <Badge key={variable} variant="secondary" className="font-mono text-xs">
                        {`{{${variable}}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
